import React, { useEffect } from "react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useLazyQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import {
  GetAssetChildrenDocument,
  GetAssetChildrenQuery,
  GetAssetChildrenQueryVariables,
} from "@hydrologiq/gql-experience/graphql"
import { Table, HStack, Text, Spinner, Box } from "@chakra-ui/react"
import { flexRender, type ColumnDef } from "@tanstack/react-table"
import { expandedRowSpacerCellStyle } from "@hydrologiq/shared/components/expandedRowStyles"

interface ExpandedAssetChildrenContainerProps {
  parentId: string
  columns: ColumnDef<gqlTypes.Asset, any>[]
}

/**
 * Container component for rendering child assets directly as rows in the parent table.
 * By rendering direct Table.Row elements into the parent Table.Body, all child cells
 * share the exact same table grid, column tracks, and layout as the parent table,
 * guaranteeing 100% pixel-perfect column alignment with headings and parent row elements.
 */
const ExpandedAssetChildrenContainer = React.memo(
  ({ parentId, columns }: ExpandedAssetChildrenContainerProps) => {
    const [executeQuery, { data, loading, error }] = useLazyQueryWithEndpoint<
      GetAssetChildrenQuery,
      GetAssetChildrenQueryVariables
    >(
      GetAssetChildrenDocument,
      { fetchPolicy: "cache-and-network" },
      "primary",
    )

    useEffect(() => {
      executeQuery({ variables: { parentId } })
    }, [parentId, executeQuery])

    const children = (data?.getAssetChildren ?? []) as gqlTypes.Asset[]

    useEffect(() => {
      const syncChildRowHeights = () => {
        const rows = document.querySelectorAll<HTMLTableRowElement>(`tr[data-child-row="${parentId}"]`)
        if (rows.length <= 1) return

        rows.forEach((row) => {
          row.style.height = ""
          Array.from(row.cells).forEach((cell) => {
            cell.style.height = ""
          })
        })

        let maxHeight = 0
        rows.forEach((row) => {
          const h = row.getBoundingClientRect().height
          if (h > maxHeight) {
            maxHeight = h
          }
        })

        if (maxHeight > 0) {
          const heightStr = `${Math.ceil(maxHeight)}px`
          rows.forEach((row) => {
            row.style.height = heightStr
            Array.from(row.cells).forEach((cell) => {
              cell.style.height = heightStr
            })
          })
        }
      }

      syncChildRowHeights()
    }, [children, columns, parentId])

    const spacerCellStyle = expandedRowSpacerCellStyle

    if (loading) {
      return (
        <Table.Row style={{ background: "#F1F5F9" }}>
          <Table.Cell style={spacerCellStyle} />
          <Table.Cell
            colSpan={columns.length}
            style={{
              padding: "0.55rem 0.625rem",
              paddingLeft: "0.125rem",
              background: "#F1F5F9",
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            <HStack gap={2} alignItems="center">
              <Spinner size="xs" color="gray.500" />
              <Text fontSize="0.75rem" color="gray.500">
                Loading sub-modules...
              </Text>
            </HStack>
          </Table.Cell>
        </Table.Row>
      )
    }

    if (error) {
      return (
        <Table.Row style={{ background: "#F1F5F9" }}>
          <Table.Cell style={spacerCellStyle} />
          <Table.Cell
            colSpan={columns.length}
            style={{
              padding: "0.55rem 0.625rem",
              paddingLeft: "0.125rem",
              background: "#F1F5F9",
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            <Text fontSize="0.75rem" color="red.500">
              Failed to load sub-modules
            </Text>
          </Table.Cell>
        </Table.Row>
      )
    }

    if (children.length === 0) {
      return (
        <Table.Row style={{ background: "#F1F5F9" }}>
          <Table.Cell style={spacerCellStyle} />
          <Table.Cell
            colSpan={columns.length}
            style={{
              padding: "0.55rem 0.625rem",
              paddingLeft: "0.125rem",
              background: "#F1F5F9",
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            <Text fontSize="0.75rem" color="gray.500" fontStyle="italic">
              No sub-modules found
            </Text>
          </Table.Cell>
        </Table.Row>
      )
    }

    return (
      <>
        {children.map((child, childIndex) => (
          <Table.Row
            key={child.id}
            data-child-row={parentId}
            style={{
              background: "#F1F5F9",
            }}
            _hover={{ bg: "#E2E8F0" }}
          >
            {/* Column 0: Spacer cell matching parent expander column */}
            <Table.Cell style={spacerCellStyle} />

            {/* Columns 1..N: Consumer columns matching parent table */}
            {columns.map((col, colIndex) => {
              const isNextToExpand = colIndex === 0
              const getValue = () => {
                if (typeof (col as any).accessorFn === "function") {
                  return (col as any).accessorFn(child)
                }
                const key = (col as any).accessorKey ?? col.id
                return key ? (child as any)[key] : undefined
              }

              const context: any = {
                row: { original: { ...child, parentId: child.parentId ?? parentId }, index: childIndex },
                getValue,
                column: col,
                table: {},
              }

              let cellContent: React.ReactNode = null
              if (col.cell) {
                cellContent = flexRender(col.cell, context)
              } else {
                const val = getValue()
                cellContent = val != null ? <Text>{String(val)}</Text> : null
              }

              return (
                <Table.Cell
                  key={col.id ?? colIndex}
                  style={{
                    ...((col.meta as any)?.shrink
                      ? { width: "max-content", whiteSpace: "nowrap" }
                      : {
                          ...(col.size ? { width: col.size, maxWidth: col.size } : {}),
                          ...(col.minSize ? { minWidth: col.minSize } : {}),
                        }),
                    padding: "0.55rem 0.625rem",
                    ...(isNextToExpand ? { paddingLeft: "0.125rem" } : {}),
                    borderBottom: "1px solid #E2E8F0",
                  }}
                >
                  <Box
                    minW={0}
                    width="100%"
                    overflow={isNextToExpand ? "visible" : "hidden"}
                    textOverflow="ellipsis"
                  >
                    {cellContent}
                  </Box>
                </Table.Cell>
              )
            })}
          </Table.Row>
        ))}
      </>
    )
  },
  (prevProps, nextProps) =>
    prevProps.parentId === nextProps.parentId && prevProps.columns === nextProps.columns
)

ExpandedAssetChildrenContainer.displayName = "ExpandedAssetChildrenContainer"

export default ExpandedAssetChildrenContainer
